import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import MemberCallToAction from '../MemberCallToAction';
import CardChip from '../CardChip';
import TrashIcon from '@icons/TrashIcon';
import CalendarPlusIcon from '@icons/CalendarPlusIcon';
import FavoriteIcon from '@icons/FavoriteIcon';
import MailIcon from '@icons/MailIcon';
import CardMemberAvatar from '@atoms/CardMemberAvatar';
import { BodySmall, ButtonNoStyles, H4 } from '@styled';

// Helpers
import { hasNewProperties, isNewMatch } from '@general/helpers/member.helpers';
import getFavoriteHook from '@hooks/getFavoriteHook';

import useIcscTracking from '@tracking/useIcscTracking';

const MemberCardRedesign = props => {
    const {
        // Member properties
        businessType,
        companyID,
        companyName,
        companyURL,
        createdAt,
        designations,
        displayLocation,
        firstName,
        fullName,
        graphID,
        id,
        lastName,
        meetingInvites,
        photoURL,
        status,
        title,
        url,
        savedSearches,

        // Everything else
        customStyle = {},
        favorited = () => {},
        hit,
        isFavorite,
        isFavoriteItemQuery = '',
        isLoggedIn = true,
        onCalendarClick,
        onClick,
        onMessageSent = () => {},
        onTrashClick,
        showCalendarIcon = false,
        showFavoriteIcon = true,
        showMailIcon = true,
        showTrashIcon = false,
        unfavorited,
    } = props;

    const history = useHistory();
    const { trackEvent } = useIcscTracking();

    // -----------------------------
    // State
    // -----------------------------

    // the useFavorite returns an isFavorite var, but sometimes it updates it in the hook and doesn't rerender
    // (when a modal has a member and so does the page)
    // moved to use the wrapper and the useIsContactFavorite - there is a slight delay when
    // the favorite is first favorited, but is accurate. And is separate, which seems beneficial.
    const useFavorite = getFavoriteHook({
        type: 'contact',
        initialIsFavorite: isFavorite,
        readItemQuery: isFavoriteItemQuery,
    });
    const { isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();

    // -----------------------------
    // Helpers
    // -----------------------------

    const onFavorite = async () => {
        if (isFavorite) {
            await removeFavorite(id);

            if (unfavorited) {
                unfavorited(id);
            }
        } else {
            saveFavorite(id);
            favorited(props);
        }
    };

    const onMessage = () => {
        onMessageSent(props);
        console.log(`Sending message to user : ${id}`);

        trackEvent({
            type: 'click',
            target: 'send_a_message_modal_mail_icon_button',
            value: `Messaged ${firstName} ${lastName}`,
        });
    };

    // -----------------------------
    // Render
    // -----------------------------

    return (
        <div className="card mb-0" style={{ height: '100%', flexGrow: 1 }}>
            <div className="p-4">
                <div className="d-flex">
                    <div className="mr-3">
                        <CardMemberAvatar
                            firstName={firstName}
                            lastName={lastName}
                            fullName={fullName}
                            photoURL={photoURL}
                            sizeDesktop={80}
                        />
                    </div>

                    <div className="flex-grow w-100">
                        <H4 className="mb-0 member-name display6 bold">
                            <a
                                className="d-inline-block align-middle mr-2 profile-name-link"
                                href={`/member/profile/${id}`}
                                onClick={e => {
                                    onClick();

                                    // History is undefined if called from within a modal
                                    if (!history) {
                                        return;
                                    }

                                    e.preventDefault();
                                    history.push(`/member/profile/${id}`, { graphID });
                                }}
                            >
                                {fullName} {designations && <span>{designations}</span>}
                            </a>
                        </H4>

                        {isNewMatch(hit) && !hasNewProperties() && (
                            <div className="d-inline-block align-middle">
                                <CardChip>New Connection</CardChip>
                            </div>
                        )}

                        {hasNewProperties() && !isNewMember(createdAt) && (
                            <div className="d-inline-block align-middle">
                                <CardChip>New Property</CardChip>
                            </div>
                        )}

                        <BodySmall className="mt-2 text-gray-dark">{title}</BodySmall>
                        <BodySmall className="text-gray-dark">{displayLocation}</BodySmall>

                        {(companyID || companyName) && (
                            <BodySmall className="text-gray-dark">
                                <a
                                    href={`/company/profile/${companyID}`}
                                    className="text-gray-dark"
                                    onClick={e => {
                                        // History is undefined if called from within a modal
                                        if (!history) {
                                            return;
                                        }

                                        e.preventDefault();
                                        history.push(`/company/profile/${companyID}`, { inApp: true });
                                    }}
                                >
                                    {companyName +
                                        (businessType && businessType.length > 0 ? ` (${businessType[0]})` : '')}
                                </a>
                            </BodySmall>
                        )}

                        <div className="mt-2">
                            <MemberCallToAction id={id} graphID={graphID} onClick={() => onClick(props)} />
                        </div>
                    </div>

                    <div className="pt-1">
                        <div className="d-flex align-items-center">
                            {showCalendarIcon && meetingInvites && (
                                <div className="mr-1.5rem">
                                    <ButtonNoStyles
                                        type="button"
                                        onClick={() => {
                                            onCalendarClick({
                                                id,
                                                graphID,
                                                firstName,
                                                lastName,
                                                fullName,
                                                companyID,
                                                companyName,
                                                title,
                                                designations,
                                                displayLocation,
                                                businessType,
                                                photoURL,
                                                url,
                                                companyURL,
                                                createdAt,
                                                isFavorite,
                                                objectID: hit.objectID,
                                                __queryID: hit.__queryID,
                                            });
                                        }}
                                    >
                                        <CalendarPlusIcon />
                                    </ButtonNoStyles>
                                </div>
                            )}

                            {showMailIcon && (
                                <MailIcon
                                    id={id}
                                    graphID={graphID}
                                    companyID={companyID}
                                    companyName={companyName}
                                    title={title}
                                    fullName={fullName}
                                    firstName={firstName}
                                    lastName={lastName}
                                    designations={designations}
                                    displayLocation={displayLocation}
                                    businessType={businessType}
                                    isFavorite={isFavorite}
                                    isLoggedIn={isLoggedIn}
                                    photoURL={photoURL}
                                    onMessage={onMessage}
                                    status={status}
                                />
                            )}

                            {showTrashIcon && (
                                <div>
                                    <ButtonNoStyles
                                        className="p-2 fill-dark"
                                        type="button"
                                        tabIndex={0}
                                        onClick={onTrashClick}
                                        style={{ lineHeight: '0' }}
                                    >
                                        <TrashIcon />
                                        <span className="sr-only">Remove</span>
                                    </ButtonNoStyles>
                                </div>
                            )}

                            <div>
                                <FavoriteIcon
                                    id={id}
                                    type="member"
                                    isLoggedIn={isLoggedIn}
                                    isFavorite={isFavorite}
                                    isLoading={isLoadingFavorite}
                                    tooltipLabel="Contact"
                                    onClick={onFavorite}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

MemberCardRedesign.propTypes = {
    // Member properties
    businessType: PropTypes.array,
    companyID: PropTypes.number,
    companyName: PropTypes.string,
    companyURL: PropTypes.string,
    createdAt: PropTypes.string,
    designations: PropTypes.string.isRequired,
    displayLocation: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    graphID: PropTypes.string,
    id: PropTypes.number.isRequired,
    lastName: PropTypes.string.isRequired,
    meetingInvites: PropTypes.bool,
    photoURL: PropTypes.string,
    status: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
    savedSearches: PropTypes.object,

    // Everything else
    customStyle: PropTypes.object,
    favorited: PropTypes.func,
    hit: PropTypes.object,
    isFavorite: PropTypes.bool.isRequired,
    isFavoriteItemQuery: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    onCalendarClick: PropTypes.func,
    onClick: PropTypes.func,
    onMessageSent: PropTypes.func,
    onTrashClick: PropTypes.func,
    showCalendarIcon: PropTypes.bool,
    showFavoriteIcon: PropTypes.bool,
    showMailIcon: PropTypes.bool,
    showTrashIcon: PropTypes.bool,
    unfavorited: PropTypes.func,
};

export default MemberCardRedesign;
